import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import ProjectCard from '@/components/projects/ProjectCard';
import SEO from '@/components/SEO';
import { PROJECTS } from '@/lib/constants';
import { Project } from '@/types/project';

interface LoadedImages {
    [key: string]: boolean;
}

const Projects = () => {
    const [imagesLoaded, setImagesLoaded] = useState<LoadedImages>({});
    const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<'mobile' | 'web' | null>(null);

    const handleImageLoad = (index: number) => {
        setImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    };

    const handleMouseEnter = () => {
        // Optional: Add hover effects if needed
    };

    const handleMouseLeave = () => {
        // Optional: Add hover effects if needed
    };

    // Get unique industries from projects
    const industries = useMemo(() => {
        const uniqueIndustries = new Set(PROJECTS.map(project => project.industry));
        return Array.from(uniqueIndustries);
    }, []);

    // Filter projects based on selected filters
    const filteredProjects = useMemo(() => {
        return PROJECTS.filter(project => {
            const matchesIndustry = !selectedIndustry || project.industry === selectedIndustry;
            const matchesType = !selectedType || project.type === selectedType;
            return matchesIndustry && matchesType;
        });
    }, [selectedIndustry, selectedType]);

    return (
        <>
            <SEO
                title="Nos Projets | Portfolio de réalisations Orcsys"
                description="Découvrez notre portfolio de projets réussis. Applications web, mobiles et solutions SaaS développées par Orcsys. Explorez nos études de cas et réalisations dans divers secteurs d'activité."
                keywords="portfolio, projets, réalisations, applications web, applications mobiles, SaaS, études de cas, transformation digitale, innovation technologique, développement sur mesure"
                type="portfolio"
                url="https://innov.orcsys.fr/projets"
                image="https://innov.orcsys.fr/images/projects-preview.jpg"
            />
            <div className="min-h-screen bg-slate-50 pt-24">
                <div className="container px-4 mx-auto py-12">
                    <div className="max-w-5xl mx-auto">
                        {/* Header */}
                        <div className="text-center mb-8 max-w-4xl mx-auto">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <h1 className="text-4xl font-bold text-slate-900 mb-4">
                                    Nos <span className="text-orcsys-500">Projets</span>
                                </h1>
                                <p className="text-lg text-slate-600">
                                    Découvrez quelques-unes de nos réalisations récentes et comment
                                    nous aidons nos clients à réussir leur transformation numérique.
                                </p>
                            </motion.div>
                        </div>

                        {/* Filters */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="mb-32 max-w-4xl mx-auto"
                        >
                            <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl shadow-slate-200/50 border border-white/20 p-8">
                                <div className="relative">
                                    {/* Background gradient effects */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-orcsys-500/10 to-purple-500/10 rounded-3xl opacity-30" />

                                    {/* Content */}
                                    <div className="relative space-y-8">
                                        {/* Industry Filter */}
                                        <div>
                                            <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center">
                                                <svg className="w-5 h-5 mr-2 text-orcsys-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                </svg>
                                                Filtrer par industrie
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => setSelectedIndustry(null)}
                                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${!selectedIndustry
                                                            ? 'bg-gradient-to-r from-orcsys-500 to-orcsys-600 text-white shadow-lg shadow-orcsys-500/25 scale-105 hover:shadow-orcsys-500/30 hover:scale-105'
                                                            : 'bg-white/80 text-slate-600 hover:bg-white hover:shadow-md hover:scale-105'
                                                        }`}
                                                >
                                                    Tous
                                                </button>
                                                {industries.map((industry) => (
                                                    <button
                                                        key={industry}
                                                        onClick={() => setSelectedIndustry(industry)}
                                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${selectedIndustry === industry
                                                                ? 'bg-gradient-to-r from-orcsys-500 to-orcsys-600 text-white shadow-lg shadow-orcsys-500/25 scale-105 hover:shadow-orcsys-500/30 hover:scale-105'
                                                                : 'bg-white/80 text-slate-600 hover:bg-white hover:shadow-md hover:scale-105'
                                                            }`}
                                                    >
                                                        {industry}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Type Filter */}
                                        <div>
                                            <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center">
                                                <svg className="w-5 h-5 mr-2 text-orcsys-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                Filtrer par plateforme
                                            </h3>
                                            <div className="flex flex-wrap gap-3">
                                                <button
                                                    onClick={() => setSelectedType(null)}
                                                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${!selectedType
                                                            ? 'bg-gradient-to-r from-orcsys-500 to-orcsys-600 text-white shadow-lg shadow-orcsys-500/25 scale-105 hover:shadow-orcsys-500/30 hover:scale-105'
                                                            : 'bg-white/80 text-slate-600 hover:bg-white hover:shadow-md hover:scale-105'
                                                        }`}
                                                >
                                                    <span className="relative">Tous</span>
                                                </button>
                                                <button
                                                    onClick={() => setSelectedType('web')}
                                                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${selectedType === 'web'
                                                            ? 'bg-gradient-to-r from-orcsys-500 to-orcsys-600 text-white shadow-lg shadow-orcsys-500/25 scale-105 hover:shadow-orcsys-500/30 hover:scale-105'
                                                            : 'bg-white/80 text-slate-600 hover:bg-white hover:shadow-md hover:scale-105'
                                                        }`}
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                                    </svg>
                                                    Web
                                                </button>
                                                <button
                                                    onClick={() => setSelectedType('mobile')}
                                                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${selectedType === 'mobile'
                                                            ? 'bg-gradient-to-r from-orcsys-500 to-orcsys-600 text-white shadow-lg shadow-orcsys-500/25 scale-105 hover:shadow-orcsys-500/30 hover:scale-105'
                                                            : 'bg-white/80 text-slate-600 hover:bg-white hover:shadow-md hover:scale-105'
                                                        }`}
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                    </svg>
                                                    Mobile
                                                </button>
                                            </div>
                                        </div>

                                        {/* Results count */}
                                        <div className="pt-2">
                                            <p className="text-sm text-slate-600 flex items-center">
                                                <svg className="w-4 h-4 mr-2 text-orcsys-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                                </svg>
                                                {filteredProjects.length} projet{filteredProjects.length > 1 ? 's' : ''} trouvé{filteredProjects.length > 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Projects List */}

                        <div className="grid grid-cols-1 gap-40 max-w-5xl mx-auto">
                            {filteredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    <ProjectCard
                                        project={project as Project}
                                        index={index}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Projects; 